import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { FC, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useBoolean } from '../../../hooks/use-boolean';
import { useIsTouch } from '../../../hooks/use-is-touch';

import styles from './OverflowingTextTip.module.scss';

type OverflowingTextTipProps = {
  className?: string;
  children: NonNullable<ReactNode>;
  lineCount?: number;
};

export const OverflowingTextTip: FC<OverflowingTextTipProps> = (props) => {
  const { className, children, lineCount = 1 } = props;

  const [isOverflowing, setIsOverflowing] = useState(false);
  const { value: isTooltipShown, setTrue: showTooltip, setFalse: hideTooltip } = useBoolean(false);

  const rootRef = useRef<HTMLDivElement>(null);

  const isTouch = useIsTouch();

  const checkOverflow = useCallback(() => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    setIsOverflowing(root.offsetWidth < root.scrollWidth);
  }, []);

  const node = useMemo(() => {
    return (
      <div
        className={classNames(styles.root, { [styles.oneLine]: lineCount === 1 }, className)}
        ref={rootRef}
        style={{
          '--lineClamp': lineCount,
        }}
        onClick={isTouch ? showTooltip : undefined}
      >
        {children}
      </div>
    );
  }, [children, className, isTouch, lineCount, showTooltip]);

  useLayoutEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);

    observer.observe(root);

    return () => {
      observer.disconnect();
    };
  }, [checkOverflow]);

  useLayoutEffect(() => {
    if (!isOverflowing) {
      hideTooltip();
    }
  }, [hideTooltip, isOverflowing]);

  return isOverflowing ? (
    <Tooltip open={isTooltipShown} title={children} onOpen={showTooltip} onClose={hideTooltip}>
      {node}
    </Tooltip>
  ) : (
    node
  );
};
